import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DefaultCustomField } from 'src/constants/types';
import { MenuItemDivider } from './MenuItemDivider.styles';
import { useCampaignsApi } from 'src/modules/campaigns/api';
import { SurveyReply } from 'src/modules/campaigns/types';
import { FormMessage } from '../../types';
import SurveyRepliesList from './SurveyRepliesList';
import { CONFIRMATION_REMINDER_TEXT, DATE_CAPTURE_FORMAT_NOTE } from './messageFormText';
import { CONTROL_HEIGHT } from './controlSizing';

const CAPTURE_SURVEY_VALUE = 'survey';
const CAPTURE_TOOLTIP =
  'If selected, any valid contact response will update the specified contact metadata.';
/** The reminder copy the confirmation instructions are appended to. */
const CALL_US_LINE = 'To make changes please call.';

/** Fields a reply can be written to: contact-scoped, minus the phone number. */
const captureTargets = (metadata: DefaultCustomField[]) => metadata
  .filter(m => m.name.startsWith('contact.') && m.name !== 'contact.standard.phone_number')
  .sort((a, b) => a.description.toLocaleLowerCase().localeCompare(b.description.toLowerCase()));

const isDateField = (metadata: DefaultCustomField[], name?: string | null) =>
  !!name && metadata.some(m => m.name === name && m.type === 'date');

interface DataCaptureSectionProps {
  message: FormMessage;
  onChange: (next: Partial<FormMessage>) => void;
  metadata: DefaultCustomField[];
  surveyEnabled?: boolean;
  captureEnabled?: boolean;
  isReminder?: boolean;
  isAnnual?: boolean;
  isJoin?: boolean;
  disabled?: boolean;
  companyId?: string;
}

/**
 * What the campaign does with a reply. The config picks the block:
 * survey only, capture only, or one combined row that offers both.
 */
export const DataCaptureSection: React.FC<DataCaptureSectionProps> = ({
  message,
  onChange,
  metadata,
  surveyEnabled = true,
  captureEnabled = true,
  isReminder = false,
  isAnnual = false,
  isJoin = false,
  disabled = false,
  companyId,
}) => {
  const [repliesOpen, setRepliesOpen] = useState(false);
  const [replies, setReplies] = useState<SurveyReply[]>([]);
  const [repliesLoaded, setRepliesLoaded] = useState(false);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [repliesError, setRepliesError] = useState<string | null>(null);
  const api = useCampaignsApi();

  useEffect(() => {
    setRepliesOpen(false);
    setReplies([]);
    setRepliesLoaded(false);
    setRepliesLoading(false);
    setRepliesError(null);
  }, [message.id]);

  const handleRepliesToggle = async () => {
    if (repliesOpen) {
      setRepliesOpen(false);
      return;
    }
    setRepliesOpen(true);
    if (repliesLoaded) return;
    if (!companyId || !message.id) {
      setRepliesError('Survey replies are unavailable for this message.');
      setRepliesLoaded(true);
      return;
    }
    setRepliesLoading(true);
    setRepliesError(null);
    try {
      const response = await api.getSurveyMessageReplies({
        companyId, scheduledMessageId: message.id, limit: 50, offset: 0,
      });
      setReplies(response.data.items ?? []);
    } catch (error) {
      setRepliesError('Unable to load survey replies.');
    } finally {
      setRepliesLoading(false);
      setRepliesLoaded(true);
    }
  };

  // Replies only exist on a campaign that has already run, which is exactly when
  // the form is read-only.
  const showReplies = disabled && !!message.isSurvey;
  const repliesButton = (
    <Button
      size="small"
      variant="text"
      onClick={() => void handleRepliesToggle()}
      disabled={repliesLoading || !companyId || !message.id}
    >
      {repliesOpen ? 'Hide replies' : 'Show replies'}
    </Button>
  );
  const repliesList = (
    <SurveyRepliesList
      open={repliesOpen}
      loading={repliesLoading}
      loaded={repliesLoaded}
      error={repliesError}
      replies={replies}
    />
  );

  // A reminder captures one thing only: whether the appointment is confirmed.
  if (isReminder && !isJoin) {
    if (!captureEnabled) return null;

    const text = message.text ?? '';
    const handleConfirmationToggle = (checked: boolean) => onChange({
      dataCapture: checked ? 'appointment.confirmation_status' : undefined,
      text: checked
        ? (text.endsWith(CALL_US_LINE) ? text + CONFIRMATION_REMINDER_TEXT : text)
        : text.replace(CONFIRMATION_REMINDER_TEXT, '') || '',
    });

    return (
      <Stack direction="column" spacing={1}>
        <Stack direction="row" alignItems="center" sx={{ marginTop: '-10px' }}>
          <FormGroup>
            <FormControlLabel
              sx={{ marginRight: 0 }}
              control={(
                <Checkbox
                  disabled={disabled}
                  name="isCaptureContact"
                  checked={!!message.dataCapture}
                  onChange={e => handleConfirmationToggle(e.target.checked)}
                />
              )}
              label="Include appointment confirmation request"
            />
          </FormGroup>
        </Stack>
      </Stack>
    );
  }

  // An annual message goes out on a date; there is no reply to capture.
  if (isAnnual) return null;
  if (!surveyEnabled && !captureEnabled) return null;

  if (!captureEnabled) {
    return (
      <Stack direction="column" sx={{ marginBottom: '10px' }}>
        <Stack direction="row" alignItems="center" sx={{ width: '100%', justifyContent: 'space-between' }}>
          <FormGroup>
            <FormControlLabel
              sx={{ marginRight: 0 }}
              control={(
                <Checkbox
                  disabled={disabled}
                  name="isSurvey"
                  checked={!!message.isSurvey}
                  onChange={e => onChange({ isSurvey: e.target.checked })}
                />
              )}
              label="Capture response to message as survey answer"
            />
          </FormGroup>
          {showReplies && repliesButton}
        </Stack>
        {showReplies && repliesList}
      </Stack>
    );
  }

  // With capture on, one row does the work. When survey is on too, the dropdown
  // gains a "Survey Answer" choice above the metadata fields and the checkbox
  // stands for either.
  const checked = surveyEnabled
    ? !!message.isSurvey || !!message.isCaptureContact
    : !!message.isCaptureContact;

  const selected = message.isSurvey && surveyEnabled
    ? CAPTURE_SURVEY_VALUE
    : ((surveyEnabled && !message.isCaptureContact) ? '' : message.dataCapture ?? '');

  const handleToggle = (on: boolean) => onChange(surveyEnabled
    // The combined row starts blank: the dropdown next to it makes the choice.
    ? { isSurvey: false, isCaptureContact: on, dataCapture: null }
    // Capture-only keeps the field the user already picked.
    : { dataCapture: on ? message.dataCapture ?? null : null, isCaptureContact: on });

  const handleSelect = ({ target }: SelectChangeEvent<string>) => {
    const value = target.value || null;
    const isSurvey = value === CAPTURE_SURVEY_VALUE;
    onChange({
      isSurvey,
      isCaptureContact: !isSurvey && !!value,
      dataCapture: isSurvey ? null : value,
    });
  };

  return (
    <Stack direction="column" sx={{ marginBottom: '10px' }}>
      <Stack direction="row" alignItems="center" sx={{ marginBottom: '10px' }}>
        <Tooltip
          placement="top"
          arrow
          slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [0, -10] } }] } }}
          title={CAPTURE_TOOLTIP}
        >
          <FormGroup>
            <FormControlLabel
              sx={{ marginRight: 0 }}
              control={(
                <Checkbox
                  disabled={disabled}
                  name={surveyEnabled ? 'captureResponse' : 'isCaptureContact'}
                  checked={checked}
                  onChange={e => handleToggle(e.target.checked)}
                />
              )}
              label="Capture response to message as"
            />
          </FormGroup>
        </Tooltip>
        {metadata && (
        <FormControl sx={{ width: '30%', marginLeft: '10px' }}>
          <Select
            sx={{ height: CONTROL_HEIGHT }}
            MenuProps={{ disableScrollLock: true }}
            disabled={disabled}
            value={selected}
            onChange={handleSelect}
            name="dataCapture"
          >
            {surveyEnabled && <MenuItem value={CAPTURE_SURVEY_VALUE}>Survey Answer</MenuItem>}
            {surveyEnabled && (
              <MenuItemDivider disabled key="capture-divider" />
            )}
            {captureTargets(metadata).map(field => (
              <MenuItem value={field.name} key={field.name}>{field.description}</MenuItem>
            ))}
          </Select>
        </FormControl>
        )}
      </Stack>

      {isDateField(metadata, message.dataCapture) && (
        <Typography mt={0.5} fontStyle="italic" fontSize={14}>
          {DATE_CAPTURE_FORMAT_NOTE}
        </Typography>
      )}

      {surveyEnabled && showReplies && repliesList}
    </Stack>
  );
};

export default DataCaptureSection;
